import { format as formatDate, formatDistanceToNowStrict } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const TZ = 'Asia/Kathmandu';

const NPR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'NPR',
  maximumFractionDigits: 0,
});
const NPR2 = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'NPR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const PCT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const COMPACT = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const NUM = new Intl.NumberFormat('en-IN');

export const fmt = {
  npr: (value: number | string | null | undefined, exact = false): string => {
    const n = toNumber(value);
    if (n === null) return '—';
    return exact ? NPR2.format(n) : NPR.format(n);
  },
  pct: (value: number | string | null | undefined): string => {
    const n = toNumber(value);
    if (n === null) return '—';
    return PCT.format(n > 1 ? n / 100 : n);
  },
  compact: (value: number | string | null | undefined): string => {
    const n = toNumber(value);
    if (n === null) return '—';
    return COMPACT.format(n);
  },
  number: (value: number | string | null | undefined): string => {
    const n = toNumber(value);
    if (n === null) return '—';
    return NUM.format(n);
  },
  delta: (value: number | string | null | undefined, opts?: { unit?: 'pct' | 'num' }): string => {
    const n = toNumber(value);
    if (n === null) return '—';
    const sign = n > 0 ? '+' : n < 0 ? '−' : '';
    const abs = Math.abs(n);
    if (opts?.unit === 'pct') return `${sign}${PCT.format(abs > 1 ? abs / 100 : abs)}`;
    return `${sign}${NUM.format(abs)}`;
  },
  date: (value: string | Date | null | undefined, pattern = 'd MMM, yyyy'): string => {
    if (!value) return '—';
    return formatInTimeZone(value, TZ, pattern);
  },
  time: (value: string | Date | null | undefined, pattern = 'HH:mm'): string => {
    if (!value) return '—';
    return formatInTimeZone(value, TZ, pattern);
  },
  datetime: (value: string | Date | null | undefined): string => {
    if (!value) return '—';
    return formatInTimeZone(value, TZ, 'd MMM, HH:mm');
  },
  ago: (value: string | Date | null | undefined): string => {
    if (!value) return '—';
    const d = typeof value === 'string' ? new Date(value) : value;
    return `${formatDistanceToNowStrict(d)} ago`;
  },
  duration: (seconds: number | null | undefined): string => {
    if (seconds == null || Number.isNaN(seconds)) return '—';
    const s = Math.max(0, Math.round(seconds));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${s % 60}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  },
  bytes: (bytes: number | null | undefined): string => {
    if (bytes == null || !Number.isFinite(bytes)) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  },
};

export const TIMEZONE = TZ;
export { formatDate };

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}
